import React from 'react'
import { useTranslation } from 'react-i18next'
import { ImageIcon } from '@node-space/storybook-components/dist/ImageIcon'
import { NotificationType } from '@node-space/storybook-components/dist/NotificationCard'
import { SimpleTableV2, TableRowV2 } from '@node-space/storybook-components/dist/SimpleTableV2'
import { StatusPill } from '@node-space/storybook-components/dist/StatusPill'
import { TablePopoverMenu } from 'components/TablePopoverMenu/TablePopoverMenu'
import { getMaskedValue, titleCase } from 'utils/utils'

type BankAccountsType = {
  id: number
  bank: string
  accountHolderName: string
  accountNumber: string
  currency: string
  status: string
}

interface BankAccountsTableV2Props {
  bankAccounts: BankAccountsType[]
  setSelectedAccount: (value: number) => void
  canEdit: boolean
}

const rowHeaderWidth = 'px-8 w-1/5'

const cellDatatStyle = 'p-4'

export const BankAccountsTableV2 = ({
  bankAccounts,
  setSelectedAccount,
  canEdit,
}: BankAccountsTableV2Props) => {
  const { t } = useTranslation()

  const getStatusPillState = (status: string): NotificationType => {
    const statusTypes = {
      VERIFIED: NotificationType.SUCCESS,
      NOT_VERIFIED: NotificationType.WARNING,
      default: NotificationType.WARNING,
    }
    return statusTypes[status] || statusTypes['default']
  }

  const bankAccountsTableData = (): TableRowV2[] => {
    return bankAccounts?.map(bankAccount => {
      return {
        rowData: [
          {
            cellData: bankAccount.accountHolderName,
            className: `${cellDatatStyle} px-8`,
          },
          {
            cellData: bankAccount.bank,
            className: rowHeaderWidth,
          },
          {
            cellData: getMaskedValue(bankAccount.accountNumber),
          },
          {
            cellData: (
              <div className="flex flex-row items-center">
                <div className="my-5 ml-1 mr-3 h-5 w-5">
                  <ImageIcon
                    width={20}
                    height={20}
                    src={`${process.env.ICONS_URL}${bankAccount.currency.toLowerCase()}.svg`}
                  />
                </div>
                {bankAccount.currency.toUpperCase()}
              </div>
            ),
          },
          {
            cellData: (
              <StatusPill
                state={getStatusPillState(bankAccount?.status.toUpperCase())}
                label={titleCase(bankAccount?.status.toLowerCase())}
                fitContent
              />
            ),
          },
          {
            cellData: canEdit && bankAccount?.id && (
              <TablePopoverMenu
                stopPropagation
                hasPadding
                testid="bankAccountsTableV2PopverMenu"
                menuItems={[
                  {
                    iconName: 'TrashIcon',
                    label: t('delete'),
                    onClick: () => setSelectedAccount(bankAccount?.id),
                  },
                ]}
              />
            ),
            align: 'end',
          },
        ],
      }
    })
  }

  return (
    <SimpleTableV2
      columns={[
        {
          headerTitle: t('accountHolder'),
          className: rowHeaderWidth,
        },
        {
          headerTitle: t('bank'),
          className: rowHeaderWidth,
        },
        {
          headerTitle: t('ibanAccountNo'),
        },
        {
          headerTitle: t('currency'),
        },
        {
          headerTitle: t('status'),
        },
        {
          headerTitle: '',
          align: 'end',
        },
      ]}
      tableData={bankAccountsTableData()}
    />
  )
}
